/**
 * Returns a zero date.
 */
export function DateZero() {
  return new Date(0)
}

/**
 * Parses a string into a date if it's a string.
 * If `undefined, returns `undefined`. Intended for use in `Model.parse`.
 * If `null`, returns `null`.
 * If an invalid date, returns `undefined`.
 * @param value
 */
export function maybeParseDate<T extends string | null | undefined>(
  value: T,
): T extends null ? Date | undefined | null : Date | undefined {
  // For some reason, we have to force these conversions.
  // The caller signature is correct though.
  if (value === null) {
    return null!
  }
  if (value === '' || value === undefined) {
    return undefined!
  }
  const result = new Date(value!)
  if (Number.isNaN(result.getTime())) {
    return undefined!
  }
  return result as any
}

export function timeago(date: Date, now?: Date): string
export function timeago(
  date: Date,
  style: keyof UnitFormats,
  now?: Date,
): string
/**
 * Returns a "timeago" style string from a date (and an optional "now").
 *
 * @param date
 * @param style Can be `short` (2s) or `long` (2 seconds)
 * @param now
 */
export function timeago(
  date: Date,
  style?: keyof UnitFormats | Date,
  now = new Date(),
): string {
  if (style instanceof Date) {
    now = style
    style = 'short'
  }
  const diff = now.getTime() - date.getTime()
  return formatDuration(diff, style || 'short')
}

/**
 * Formats a duration.
 *
 * @param durationMs
 * @param style
 * @param nowText
 */
export function formatDuration(
  durationMs: number,
  style: keyof UnitFormats,
  nowText = 'now',
) {
  const seconds = durationMs / 1000
  const unitFormat = UnitFormats[style]
  return seconds < 5
    ? nowText
    : seconds < 60
      ? formatWithUnit(seconds, unitFormat.s)
      : seconds < 60 * 60
        ? formatWithUnit(seconds / 60, unitFormat.m)
        : seconds < 60 * 60 * 24
          ? formatWithUnit(seconds / (60 * 60), unitFormat.h)
          : seconds < 60 * 60 * 24 * 31
            ? formatWithUnit(seconds / (60 * 60 * 24), unitFormat.d)
            : seconds < 60 * 60 * 24 * 365
              ? formatWithUnit(seconds / (60 * 60 * 24 * 31), unitFormat.mo)
              : formatWithUnit(seconds / (60 * 60 * 24 * 365), unitFormat.y)
}

function formatWithUnit(value: number, unitPair: UnitFormatPair) {
  const floored = Math.floor(value)
  const suffix = floored === 1 ? unitPair[0] : unitPair[1]
  return `${floored}${suffix}`
}

interface UnitFormat {
  s: UnitFormatPair
  m: UnitFormatPair
  h: UnitFormatPair
  d: UnitFormatPair
  mo: UnitFormatPair
  y: UnitFormatPair
}

type UnitFormatPair = [one: string, other: string]

interface UnitFormats {
  short: UnitFormat
  long: UnitFormat
}

const UnitFormats: UnitFormats = {
  short: {
    s: ['s', 's'],
    m: ['m', 'm'],
    h: ['h', 'h'],
    d: ['d', 'd'],
    mo: ['mo', 'mo'],
    y: ['y', 'y'],
  },
  long: {
    s: [' second', ' seconds'],
    m: [' minute', ' minutes'],
    h: [' hour', ' hours'],
    d: [' day', ' days'],
    mo: [' month', ' months'],
    y: [' year', ' years'],
  },
}

/**
 * Given a number of seconds, converts it into "minutes and seconds" of the
 * form "Xm Ys".
 */
export function formattedTimeUntil(secondsRemaining: number) {
  // Note: `~~` is a double bitwise operation and it performs a fast
  // `Math.floor`.

  if (secondsRemaining < 0) {
    return '0m 0s'
  }

  const days = ~~(secondsRemaining / 86400)
  const hours = ~~((secondsRemaining % 86400) / 3600)
  const minutes = ~~((secondsRemaining % 3600) / 60)
  const seconds = ~~(secondsRemaining % 60)

  const daysString = days !== 0 ? `${days}d ` : ''
  const hoursString = hours !== 0 ? `${hours}h ` : ''

  return `${daysString}${hoursString}${minutes}m ${seconds}s`
}

/**
 * Returns a "days until" integer from a date (and an optional "now").
 *
 * @param dueDate
 * @param now
 */
export function daysuntil(dueDate: Date, now = new Date()) {
  const diff = (dueDate.getTime() - now.getTime()) / 1000

  return Math.floor(diff / (60 * 60 * 24))
}

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
})

const timeFormatter = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: 'numeric',
})

const longDateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
})

const longDateFormatterWithZone = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  timeZoneName: 'short',
})

/**
 * Formats the date to a string.
 * @param date
 * @returns `string`
 */
export function formatToLongDate(date: Date): string {
  return longDateFormatter.format(date)
}

export function formatToLongDateWithComma(date: Date): string {
  return dateFormatter.format(date) + ', ' + timeFormatter.format(date)
}

export function formatToLongDateWithTimeZone(date: Date): string {
  return longDateFormatterWithZone.format(date)
}

export function getYear(date?: Date): string {
  return (date?.getFullYear() ?? new Date().getFullYear()).toString()
}
